import { ReactNode, useState } from 'react';
import { useQuery } from 'react-query';
import { api } from '../../services/api';
import { Lead, Panel, PanelHeader, PanelBody } from '../ui/Panel';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import MoreMenu from '../ui/MoreMenu';
import Pill from '../ui/Pill';
import { EmptyState, ErrorNotice, SkeletonRows } from '../ui/States';
import { Audience, EventDetail, audienceName, dayLabel } from '../../lib/events';
import { errorMessage } from '../../lib/programs';
import { confirmAction } from '../ui/Confirm';
import { headRow, th, td, em } from './logistics';
import {
  Agenda,
  AgendaSession,
  FORMAT_LABEL,
  PartRole,
  ROLE_LABEL,
  SessionFormat,
  deliverableName,
  field,
  missingSomething,
  nextDue,
  plural,
  printedName,
  shortDate,
  stack,
  timeRange,
  useAgenda,
  useAgendaWrite,
} from './agendaTypes';

/** Where the planning-sheet import draws itself, under the tab's opening sentence. */
export const AGENDA_IMPORT_SLOT = 'agenda-import-slot';
/** Fired by "Follow up with speakers": the speakers section turns its filter on and scrolls into view. */
export const FOLLOW_UP_EVENT = 'divert:speakers-follow-up';

function Field({
  title,
  hint,
  children,
  wide = false,
}: {
  title: string;
  hint?: string;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className={wide ? field.wide : ''}>
      <label className="block">
        <span className={field.label}>{title}</span>
        {children}
      </label>
      {hint ? <p className={field.hint}>{hint}</p> : null}
    </div>
  );
}

interface SessionForm {
  day: string;
  startTime: string;
  endTime: string;
  title: string;
  titleFr: string;
  description: string;
  descriptionFr: string;
  format: SessionFormat;
  room: string;
  audiences: string[];
  hybrid: boolean;
  interpreted: boolean;
  notes: string;
  zoomLink: string;
  recordingPlanned: boolean;
}

const toForm = (s: AgendaSession | null, day: string): SessionForm => ({
  day: s?.day ?? day,
  startTime: s?.startTime ?? '09:00',
  endTime: s?.endTime ?? '',
  title: s?.title ?? '',
  titleFr: s?.titleFr ?? '',
  description: s?.description ?? '',
  descriptionFr: s?.descriptionFr ?? '',
  format: s?.format ?? 'talk',
  room: s?.room ?? '',
  audiences: s?.audiences ?? [],
  hybrid: s?.hybrid ?? false,
  interpreted: s?.interpreted ?? false,
  notes: s?.notes ?? '',
  zoomLink: s?.zoomLink ?? '',
  recordingPlanned: s?.recordingPlanned ?? false,
});

function SessionModal({
  eventId,
  days,
  audiences,
  session,
  day,
  onClose,
}: {
  eventId: string;
  days: string[];
  audiences: Audience[];
  session: AgendaSession | null;
  day: string;
  onClose: () => void;
}) {
  const [f, setF] = useState<SessionForm>(toForm(session, day));
  const set = <K extends keyof SessionForm>(k: K, v: SessionForm[K]) => setF((x) => ({ ...x, [k]: v }));
  const save = useAgendaWrite(eventId, () => {
    const body = { ...f, endTime: f.endTime || null };
    return session
      ? api.patch(`/events/agenda/sessions/${session.id}`, body)
      : api.post(`/events/${eventId}/agenda/sessions`, body);
  });
  const folded = Boolean(session?.description || session?.descriptionFr || session?.zoomLink || session?.notes);
  return (
    <Modal
      title={session ? 'Edit session' : 'Add a session'}
      why="Sessions sort by start time. Leave the audiences unticked if everyone attends that day."
      onClose={onClose}
      width={680}
      footer={
        <>
          <Button
            variant="primary"
            disabled={save.isLoading || !f.title.trim()}
            onClick={() => save.mutate(undefined, { onSuccess: onClose })}
          >
            {session ? 'Save' : 'Add the session'}
          </Button>
          <Button onClick={onClose}>Cancel</Button>
          {!f.title.trim() ? <span className="text-small text-ink-3">Give the session an English title.</span> : null}
        </>
      }
    >
      {save.isError ? (
        <div className="mb-4">
          <ErrorNotice>{errorMessage(save.error, 'Could not save the session')}</ErrorNotice>
        </div>
      ) : null}
      <div className={field.grid}>
        <Field title="Day">
          <select className={field.input} value={f.day} onChange={(e) => set('day', e.target.value)}>
            {days.map((d) => (
              <option key={d} value={d}>
                {dayLabel(d)}
              </option>
            ))}
          </select>
        </Field>
        <Field title="Format">
          <select
            className={field.input}
            value={f.format}
            onChange={(e) => set('format', e.target.value as SessionFormat)}
          >
            {Object.entries(FORMAT_LABEL).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        </Field>
        <Field title="Starts" hint="Toronto time">
          <input
            className={field.input}
            type="time"
            value={f.startTime}
            onChange={(e) => set('startTime', e.target.value)}
          />
        </Field>
        <Field title="Ends">
          <input
            className={field.input}
            type="time"
            value={f.endTime}
            onChange={(e) => set('endTime', e.target.value)}
          />
        </Field>
        <Field title="Title in English" wide>
          <input className={field.input} value={f.title} onChange={(e) => set('title', e.target.value)} />
        </Field>
        <Field title="Title in French" hint="Optional. The English title is used where it's missing." wide>
          <input className={field.input} value={f.titleFr} onChange={(e) => set('titleFr', e.target.value)} />
        </Field>
        <Field title="Room or location" wide>
          <input className={field.input} value={f.room} onChange={(e) => set('room', e.target.value)} />
        </Field>
        {audiences.length ? (
          <fieldset className={field.wide}>
            <legend className={field.label}>Audiences</legend>
            <p className={`${field.hint} mt-0 mb-1`}>
              Only for the audiences ticked; none ticked means everyone that day.
            </p>
            {audiences.map((a) => (
              <label key={a.key} className={field.check}>
                <input
                  type="checkbox"
                  className={field.box}
                  checked={f.audiences.includes(a.key)}
                  onChange={() =>
                    set(
                      'audiences',
                      f.audiences.includes(a.key) ? f.audiences.filter((k) => k !== a.key) : [...f.audiences, a.key],
                    )
                  }
                />
                {a.name}
              </label>
            ))}
          </fieldset>
        ) : null}
        <fieldset className={field.wide}>
          <legend className={field.label}>Also</legend>
          <label className={field.check}>
            <input
              type="checkbox"
              className={field.box}
              checked={f.hybrid}
              onChange={(e) => set('hybrid', e.target.checked)}
            />
            Online too (hybrid)
          </label>
          <label className={field.check}>
            <input
              type="checkbox"
              className={field.box}
              checked={f.interpreted}
              onChange={(e) => set('interpreted', e.target.checked)}
            />
            French interpretation (when the event interprets selected sessions)
          </label>
          <label className={field.check}>
            <input
              type="checkbox"
              className={field.box}
              checked={f.recordingPlanned}
              onChange={(e) => set('recordingPlanned', e.target.checked)}
            />
            Recorded
          </label>
        </fieldset>
      </div>
      <details className={`${field.fold} mt-5`} open={folded}>
        <summary className={field.summary}>Descriptions, Zoom link and staff notes</summary>
        <div className={field.grid}>
          <Field title="Description in English">
            <textarea
              className={field.input}
              rows={3}
              value={f.description}
              onChange={(e) => set('description', e.target.value)}
            />
          </Field>
          <Field title="Description in French">
            <textarea
              className={field.input}
              rows={3}
              value={f.descriptionFr}
              onChange={(e) => set('descriptionFr', e.target.value)}
            />
          </Field>
          <Field title="Zoom link" hint="For online attendees, AV and the interpreters." wide>
            <input className={field.input} value={f.zoomLink} onChange={(e) => set('zoomLink', e.target.value)} />
          </Field>
          <Field title="Notes for staff only" wide>
            <textarea className={field.input} rows={2} value={f.notes} onChange={(e) => set('notes', e.target.value)} />
          </Field>
        </div>
      </details>
    </Modal>
  );
}

/** Who is on a session: take people off it, or add a speaker from the list with a role. */
function PeopleModal({ eventId, sessionId, onClose }: { eventId: string; sessionId: string; onClose: () => void }) {
  const agenda = useAgenda(eventId);
  const session = agenda.data?.sessions.find((s) => s.id === sessionId);
  const [speakerId, setSpeakerId] = useState('');
  const [role, setRole] = useState<PartRole>('speaker');
  const assign = useAgendaWrite(eventId, () =>
    api.post(`/events/agenda/sessions/${sessionId}/speakers`, { speakerId, role }),
  );
  const unassign = useAgendaWrite(eventId, (partId: string) => api.delete(`/events/agenda/parts/${partId}`));
  const speakers = agenda.data?.speakers ?? [];
  return (
    <Modal
      title={session ? `People on "${session.title}"` : 'People on the session'}
      why="Add a speaker from the speaker list with their role, or take someone off this session. They stay on the speaker list."
      onClose={onClose}
      width={620}
      footer={<Button onClick={onClose}>Done</Button>}
    >
      {assign.isError || unassign.isError ? (
        <div className="mb-4">
          <ErrorNotice>{errorMessage(assign.error || unassign.error, 'Could not change the people')}</ErrorNotice>
        </div>
      ) : null}
      {session?.people.length ? (
        <table className="w-full tabular mb-5">
          <thead>
            <tr className={headRow}>
              <th className={th}>Person</th>
              <th className={th}>Role</th>
              <th className={th}>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {session.people.map((p) => (
              <tr key={p.partId} className="border-b border-line">
                <td className={td}>
                  <span className={p.confirmation === 'declined' ? 'line-through text-ink-3' : ''}>
                    {printedName(p)}
                  </span>
                  {p.title || p.affiliation ? (
                    <span className={em}>{[p.title, p.affiliation].filter(Boolean).join(', ')}</span>
                  ) : null}
                </td>
                <td className={td}>{ROLE_LABEL[p.role]}</td>
                <td className={`${td} text-right`}>
                  <Button
                    size="sm"
                    variant="quiet"
                    aria-label={`Take ${printedName(p)} off the session`}
                    disabled={unassign.isLoading}
                    onClick={() =>
                      void confirmAction(
                        `Take ${printedName(p)} off this session? They stay on the speaker list.`,
                        'Take them off',
                      ).then((ok) => ok && unassign.mutate(p.partId))
                    }
                  >
                    Take off
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-ink mb-5">Nobody is on this session yet.</p>
      )}
      {speakers.length ? (
        <div className="flex flex-wrap items-end gap-3">
          <label className="block flex-[1_1_220px]">
            <span className={field.label}>Speaker</span>
            <select className={field.input} value={speakerId} onChange={(e) => setSpeakerId(e.target.value)}>
              <option value="">Choose a speaker</option>
              {speakers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block flex-[0_1_160px]">
            <span className={field.label}>Role</span>
            <select className={field.input} value={role} onChange={(e) => setRole(e.target.value as PartRole)}>
              {Object.entries(ROLE_LABEL).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <Button
            disabled={!speakerId || assign.isLoading}
            onClick={() => assign.mutate(undefined, { onSuccess: () => setSpeakerId('') })}
          >
            Add to the session
          </Button>
        </div>
      ) : (
        <p className="text-ink-2">Add speakers to the speaker list first.</p>
      )}
    </Modal>
  );
}

function SessionRow({
  eventId,
  s,
  audiences,
  parallel,
  canEdit,
  onEdit,
  onPeople,
}: {
  eventId: string;
  s: AgendaSession;
  audiences: Audience[];
  parallel: boolean;
  canEdit: boolean;
  onEdit: () => void;
  onPeople: () => void;
}) {
  const move = useAgendaWrite(eventId, (direction: 'up' | 'down') =>
    api.post(`/events/agenda/sessions/${s.id}/move`, { direction }),
  );
  const remove = useAgendaWrite(eventId, () => api.delete(`/events/agenda/sessions/${s.id}`));
  const quiet = s.format === 'break' || s.format === 'meal';
  const names = s.people.map(printedName);
  const who = names.length
    ? ` ${names.length > 2 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names.join(' and ')} ${names.length === 1 ? 'is' : 'are'} taken off it and stay${names.length === 1 ? 's' : ''} on the speaker list.`
    : '';
  const facts = [
    FORMAT_LABEL[s.format],
    s.room,
    s.hybrid ? 'hybrid' : null,
    s.interpretedEffective ? 'interpreted' : null,
    s.recordingPlanned ? 'recorded' : null,
    s.zoomLink ? 'Zoom link' : null,
    s.audiences?.length ? `${s.audiences.map((k) => audienceName(audiences, k)).join(', ')} only` : null,
    s.titleFr ? null : 'no French title',
    parallel ? 'at the same time as another session' : null,
  ].filter(Boolean);
  const roles = Array.from(new Set(s.people.map((p) => p.role)));
  return (
    <tr className={`${stack.row} ${quiet ? 'bg-selected' : 'hover:bg-selected'}`}>
      <td className={`${td} ${stack.cell} text-ink-2 whitespace-nowrap`}>{timeRange(s)}</td>
      <td className={`${td} ${stack.cell} ${quiet ? 'text-ink-2' : ''}`}>
        <span className={quiet ? '' : 'font-semibold'}>{s.title}</span>
        <span className={em}>{facts.join(' · ')}</span>
        {move.isError || remove.isError ? (
          <span className="block text-small text-warning">
            {errorMessage(move.error || remove.error, 'Could not change the session')}
          </span>
        ) : null}
      </td>
      <td
        className={`${td} ${stack.cell}`}
        data-label={s.people.length ? (roles.length === 1 ? ROLE_LABEL[roles[0]] : 'People') : undefined}
      >
        {s.people.map((p) => (
          <div key={p.partId} className="mb-1 last:mb-0">
            <span className={p.confirmation === 'declined' ? 'line-through text-ink-3' : ''}>{printedName(p)}</span>
            {p.confirmation === 'tbc' ? (
              <>
                {' '}
                <Pill tone="planned" small>
                  not confirmed
                </Pill>
              </>
            ) : null}
            <span className={em}>
              {[roles.length > 1 || p.role !== 'speaker' ? ROLE_LABEL[p.role] : null, p.title, p.affiliation]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </div>
        ))}
        {!s.people.length && !quiet ? <span className="text-ink-3">–</span> : null}
      </td>
      {canEdit ? (
        <td className={`${td} ${stack.acts} text-right whitespace-nowrap`}>
          <span className="inline-flex gap-2 max-md:contents">
            <Button size="sm" onClick={onEdit} aria-label={`Edit ${s.title}`}>
              Edit
            </Button>
            <MoreMenu
              label={`More for ${s.title}`}
              items={[
                { label: 'People', onClick: onPeople },
                ...(parallel
                  ? [
                      { label: 'Move up', onClick: () => move.mutate('up') },
                      { label: 'Move down', onClick: () => move.mutate('down') },
                    ]
                  : []),
                {
                  label: 'Delete',
                  destructive: true,
                  onClick: () =>
                    void confirmAction(
                      `Delete "${s.title}"? The session leaves the agenda.${who} This can't be undone.`,
                      'Delete the session',
                    ).then((ok) => ok && remove.mutate(undefined)),
                },
              ]}
            />
          </span>
        </td>
      ) : null}
    </tr>
  );
}

/** The tab's opening sentence: sessions, days, speakers, and who is still missing something. */
function AgendaLead({ agenda, eventId }: { agenda: Agenda; eventId: string }) {
  const event = useQuery<EventDetail>(['event', eventId], async () => (await api.get(`/events/${eventId}`)).data.data);
  const ahead = !event.data || new Date(event.data.event.endsAt) >= new Date();
  const sessions = agenda.sessions.length;
  const speakers = agenda.speakers.length;
  const days = agenda.event.days.length;
  const missing = agenda.speakers.filter(missingSomething);
  const first = nextDue(missing.flatMap((s) => s.deliverableItems));
  let lead: string;
  if (!sessions && !speakers) lead = 'Nothing is on the agenda yet.';
  else {
    lead = `${plural(sessions, 'session')} over ${plural(days, 'day')}, with ${plural(speakers, 'speaker')}.`;
    if (ahead && speakers) {
      if (!missing.length) lead += ' Every speaker has what they need.';
      else {
        lead +=
          missing.length === speakers && speakers > 1
            ? ' Every speaker is still missing something'
            : ` ${plural(missing.length, 'speaker')} ${missing.length === 1 ? 'is' : 'are'} still missing something`;
        lead += first?.due
          ? first.overdue
            ? `; the first deadline, ${deliverableName(first)}, was due ${shortDate(first.due)}.`
            : `; the first deadline is ${deliverableName(first)}, due ${shortDate(first.due)}.`
          : '.';
      }
    }
  }
  return (
    <Lead
      lead={lead}
      sub="The agenda, who speaks where, and what each speaker still has to send. Interpretation and AV settings are at the bottom."
    >
      {ahead && missing.length ? (
        <Button variant="primary" onClick={() => window.dispatchEvent(new CustomEvent(FOLLOW_UP_EVENT))}>
          Follow up with speakers
        </Button>
      ) : null}
    </Lead>
  );
}

/** Day-by-day schedule builder: sessions in time order, people per session. Opens the tab with its lead. */
export default function AgendaPanel({ eventId, canEdit }: { eventId: string; canEdit: boolean }) {
  const agenda = useAgenda(eventId);
  const days = agenda.data?.event.days.map((d) => d.date) ?? [];
  const [day, setDay] = useState<string | null>(null);
  const [editing, setEditing] = useState<AgendaSession | 'new' | null>(null);
  const [people, setPeople] = useState<string | null>(null);
  const current = day && days.includes(day) ? day : days[0];
  const all = agenda.data?.sessions ?? [];
  const list = all.filter((s) => s.day === current);
  const audiences = agenda.data?.event.audiences ?? [];
  const away = audiences.filter((a) => a.days && current && !a.days.includes(current));
  const startsAt = (t: string) => list.filter((x) => x.startTime === t).length;
  return (
    <>
      {agenda.data ? <AgendaLead agenda={agenda.data} eventId={eventId} /> : null}
      <div id={AGENDA_IMPORT_SLOT} />
      <Panel className="mt-14 max-md:mt-9">
        <PanelHeader title="Agenda" note={agenda.data ? plural(all.length, 'session') : undefined}>
          {canEdit && current ? <Button onClick={() => setEditing('new')}>Add a session</Button> : null}
        </PanelHeader>
        <PanelBody>
          {agenda.isLoading ? <SkeletonRows rows={5} /> : null}
          {agenda.isError ? <ErrorNotice>{errorMessage(agenda.error, 'Could not load the agenda')}</ErrorNotice> : null}
          {agenda.data ? (
            <>
              {days.length > 1 ? (
                <div role="tablist" aria-label="Day" className="flex gap-6 border-b border-line mb-3 overflow-x-auto">
                  {days.map((d) => {
                    const label = agenda.data.event.days.find((x) => x.date === d)?.label;
                    const selected = d === current;
                    return (
                      <button
                        key={d}
                        role="tab"
                        aria-selected={selected}
                        onClick={() => setDay(d)}
                        className={`py-[10px] text-[15px] leading-5 whitespace-nowrap ${
                          selected
                            ? 'text-ink font-semibold shadow-[inset_0_-2px_0_#2A2420]'
                            : 'text-ink-2 hover:text-ink'
                        }`}
                      >
                        {dayLabel(d)}
                        {label ? ` · ${label}` : ''}
                      </button>
                    );
                  })}
                </div>
              ) : null}
              {away.length ? (
                <p className="text-[15px] leading-[22px] text-ink-2 mb-2">
                  {away.map((a) => a.name).join(', ')} don&rsquo;t attend this day.
                </p>
              ) : null}
              {!list.length ? (
                <EmptyState title="No sessions on this day yet">
                  {canEdit
                    ? 'Add a session, or import the planning sheet.'
                    : 'Sessions appear here once they are added.'}
                </EmptyState>
              ) : (
                <table className={stack.table}>
                  <thead className={stack.head}>
                    <tr className={headRow}>
                      <th className={`${th} w-[160px]`}>Time</th>
                      <th className={th}>Session</th>
                      <th className={`${th} w-[34%]`}>People</th>
                      {canEdit ? (
                        <th className={`${th} w-[120px]`}>
                          <span className="sr-only">Actions</span>
                        </th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody className={stack.body}>
                    {list.map((s) => (
                      <SessionRow
                        key={s.id}
                        eventId={eventId}
                        s={s}
                        audiences={audiences}
                        parallel={startsAt(s.startTime) > 1}
                        canEdit={canEdit}
                        onEdit={() => setEditing(s)}
                        onPeople={() => setPeople(s.id)}
                      />
                    ))}
                  </tbody>
                </table>
              )}
              {canEdit && list.length ? (
                <p className="text-small text-ink-3 mt-3">
                  Sessions follow their start time. &ldquo;More&rdquo; holds People, Move up or down (for sessions at
                  the same time) and Delete.
                </p>
              ) : null}
            </>
          ) : null}
        </PanelBody>
        {editing && current ? (
          <SessionModal
            eventId={eventId}
            days={days}
            audiences={audiences}
            session={editing === 'new' ? null : editing}
            day={current}
            onClose={() => setEditing(null)}
          />
        ) : null}
        {people ? <PeopleModal eventId={eventId} sessionId={people} onClose={() => setPeople(null)} /> : null}
      </Panel>
    </>
  );
}
